// Workflow Hygiene Status Count card. Headline `count` in tier colour,
// "target ≤ 8" hint, an inline breakdown of how many statuses are in/out of
// spine, the full tag list of observed statuses (non-spine first in warning
// styling, in-spine after in subtle green), and the spine reminder.
//
// Empty state ("Standards not yet configured") triggered when the API
// returns `notConfigured: true` — i.e. the canonicalValues column is empty
// or null in the DB.

import { Badge, Card, CardContent } from '@/components/ui';
import type { IJiraSyncHealth, IMetricThresholdSet, INonCanonicalStatus, IStatusCountSection } from '@skystone/shared';
import { useMetricDefinition } from '@/hooks/use-metric-definition';
import { getTierColorClass } from '@/lib/metric-color-class';
import { cn } from '@/lib/utils';
import { JiraSyncHealthDot } from '@/components/shared/JiraSyncHealthDot';
import { useOpenDrill } from './drill/use-open-drill';

interface StatusCountCardProps {
  data: IStatusCountSection | { notConfigured: true };
  /**
   * Per-project Jira sync health threaded in from `WorkflowHygieneGroup`.
   * Optional during the rollout window before US4 (PRODOPSAI-429) wires the
   * producer side — when absent, the title row renders without the dot.
   */
  syncHealth?: IJiraSyncHealth;
}

/**
 * Status Count is `lower_better`, so the success tier is the one with
 * `lowerBound === null` (i.e. -∞). Its `upperBound` is the threshold an
 * admin can edit (8 by default). Returns `null` when the threshold set
 * is missing or the success tier has no upperBound — caller hides the
 * hint in that case rather than rendering a stale literal.
 */
function lowerBetterTargetUpperBound(set: IMetricThresholdSet | null): number | null {
  if (!set) return null;
  const successTier = set.tiers.find((t) => t.colorToken === 'success');
  return successTier?.upperBound ?? null;
}

export function StatusCountCard({ data, syncHealth }: Readonly<StatusCountCardProps>) {
  const definition = useMetricDefinition('statusCount');
  const openDrill = useOpenDrill();
  const thresholdSet: IMetricThresholdSet | null = definition
    ? { metricKey: definition.metricKey, direction: definition.direction, tiers: definition.tiers }
    : null;
  const targetMax = lowerBetterTargetUpperBound(thresholdSet);

  if (data.notConfigured) {
    return (
      <Card className="h-full shadow-none">
        <CardContent className="space-y-2 p-4">
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Status Count
            </p>
            <JiraSyncHealthDot health={syncHealth} />
          </div>
          <p className="text-sm italic text-muted-foreground">
            Standards not yet configured
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full shadow-none">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center gap-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Status Count
          </p>
          <JiraSyncHealthDot health={syncHealth} />
        </div>
        <div className="flex items-baseline gap-2">
          <span
            className={cn('text-3xl font-semibold tabular-nums', getTierColorClass(data.count, thresholdSet))}
          >
            {data.count}
          </span>
          {targetMax !== null && (
            <span className="text-xs text-muted-foreground">target ≤ {targetMax}</span>
          )}
        </div>

        <p className="text-[11px] text-muted-foreground">
          {data.count} {data.count === 1 ? 'status' : 'statuses'} ·{' '}
          {data.nonCanonical.length} non-spine ·{' '}
          {data.inSpine.length} in spine
        </p>

        {(data.nonCanonical.length > 0 || data.inSpine.length > 0) && (
          <div className="flex flex-wrap gap-1.5" aria-label="Observed statuses">
            {data.nonCanonical.map(({ status, itemCount }: INonCanonicalStatus) => (
              <Badge key={`ns-${status}`} variant="secondary" className="font-normal">
                {status} · {itemCount}
              </Badge>
            ))}
            {data.inSpine.map(({ status, itemCount }: INonCanonicalStatus) => (
              <Badge key={`is-${status}`} variant="success" className="font-normal">
                {status} · {itemCount}
              </Badge>
            ))}
          </div>
        )}

        <p className="text-[11px] text-muted-foreground">
          Spine: {data.canonical.join(' · ')}
        </p>

        <button
          type="button"
          onClick={() => openDrill('statuses')}
          className="block text-left text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
        >
          View all →
        </button>
      </CardContent>
    </Card>
  );
}
